<template>
  <div class="flex justify-end">
    <v-btn-toggle
      v-model="simpleMode"
      border
      divided
      variant="outlined"
      mandatory
      rounded="xl"
      density="compact"
      color="primary"
    >
      <v-btn :value="true">{{ t("logs.important") }}</v-btn>
      <v-btn :value="false">{{ t("logs.all") }}</v-btn>
    </v-btn-toggle>
  </div>

  <div v-if="loadingLogs" class="flex justify-center mt-10">
    <v-progress-circular indeterminate color="primary" width="2" size="24"></v-progress-circular>
  </div>
  <div v-else-if="!formattedLogs.length" class="mt-10 text-center text-sm text-medium-emphasis">
    {{ t("logs.noLogs") }}
  </div>
  <div v-else>
    <v-timeline side="end" class="h-auto">
      <v-timeline-item
        v-for="log in formattedLogs"
        :key="log.id"
        :icon="log.icon"
        :dot-color="log.color || 'transparent'"
        size="large"
      >
        <template #opposite>
          <span class="text-xs" :title="log.formattedDate">{{ log.relativeTime }}</span>
        </template>
        <div>
          <div>{{ log.action }}</div>
          <div class="text-xs text-medium-emphasis">{{ log.formattedDate }}</div>
        </div>
      </v-timeline-item>
    </v-timeline>
  </div>
</template>

<script setup lang="ts">
import { useAccountCurrency } from "@/composables/useAccountCurrency";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { useRelativeTime } from "@/composables/useRelativeTime";
import { db } from "@/firebase";
import { formatCurrency } from "@/utils/tools";
import type { Log } from "@aircap/common/log";
import { logsFullPath } from "@aircap/common/log";
import { collection, orderBy, query, where } from "firebase/firestore";
import { computed, ref, toRef } from "vue";

const props = defineProps<{
  accountId: string;
}>();

const SIMPLE_MODE_FILTERS: Log["name"][] = [
  "openBanking.initialized",
  "openBanking.connected",
  "openBanking.suspended",
  "account.invitationSent",
  "account.invitationAccepted",
  "account.memberAdded",
  "account.memberOnboardingSubmitted",
  "account.memberActivated",
  "account.memberRemoved",
  "kycAdminReview.pending",
  "kycAdminReview.refused",
  "kycAdminReview.validated",
  "email.spammed",
  "email.clicked",
  "email.sent",
];

const simpleMode = ref<boolean>(true);

const { t, locale } = useI18n();
const accountId = toRef(props, "accountId");
const { currencyCode } = useAccountCurrency(accountId);
const { getRelativeTime } = useRelativeTime();

const logsQuery = computed(() => {
  const baseQuery = collection<Log>(db, logsFullPath(accountId.value));

  if (simpleMode.value) {
    return query(
      baseQuery,
      where<Log>("name", "in", SIMPLE_MODE_FILTERS),
      orderBy<Log>("createdAt", "desc"),
    );
  }

  return query(baseQuery, orderBy<Log>("createdAt", "desc"));
});

const { data: logs, pending: loadingLogs } = useCollection<Log>(logsQuery);

const getLogDetails = (
  log: Log,
): { category: string; action: string; color: string; icon: string } => {
  switch (log.name) {
    case "openBanking.initialized":
      return {
        category: t("logs.categories.openBanking"),
        action: t("logs.actions.openBanking.initialized"),
        color: "success",
        icon: "mdi-arrow-top-right-thin-circle-outline",
      };
    case "openBanking.returned":
      return {
        category: t("logs.categories.openBanking"),
        action: t("logs.actions.openBanking.returned"),
        color: "success",
        icon: "mdi-arrow-bottom-left-thin-circle-outline",
      };
    case "openBanking.connected":
      return {
        category: t("logs.categories.openBanking"),
        action: t("logs.actions.openBanking.connected"),
        color: "success",
        icon: "mdi-check-circle-outline",
      };
    case "openBanking.synced":
      return {
        category: t("logs.categories.openBanking"),
        action: t("logs.actions.openBanking.synced", { count: log.data.transactionsSynced }),
        color: "success",
        icon: "mdi-sync",
      };
    case "openBanking.suspended":
      return {
        category: t("logs.categories.openBanking"),
        action: t("logs.actions.openBanking.suspended"),
        color: "warning",
        icon: "mdi-alert-outline",
      };
    case "openBanking.error":
      return {
        category: t("logs.categories.openBanking"),
        action: t("logs.actions.openBanking.error", { status: log.data.status }),
        color: "error",
        icon: "mdi-alert-circle-outline",
      };
    case "account.created":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.created"),
        color: "success",
        icon: "mdi-folder-plus-outline",
      };
    case "account.archived":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.archived"),
        color: "warning",
        icon: "mdi-folder-remove-outline",
      };
    case "account.reactivated":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.reactivated"),
        color: "success",
        icon: "mdi-folder-refresh-outline",
      };
    case "account.invitationSent":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.invitationSent"),
        color: "info",
        icon: "mdi-account-arrow-right-outline",
      };
    case "account.invitationAccepted":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.invitationAccepted"),
        color: "success",
        icon: "mdi-account-check-outline",
      };
    case "account.invitationRefused":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.invitationRefused"),
        color: "warning",
        icon: "mdi-account-cancel-outline",
      };
    case "account.invitationCanceled":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.invitationCanceled"),
        color: "info",
        icon: "mdi-account-remove-outline",
      };
    case "account.memberAdded":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.memberAdded", { email: log.data.email }),
        color: "info",
        icon: "mdi-account-plus-outline",
      };
    case "account.memberOnboardingSubmitted":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.memberOnboardingSubmitted", { email: log.data.email }),
        color: "info",
        icon: "mdi-account-clock-outline",
      };
    case "account.memberActivated":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.memberActivated", { email: log.data.email }),
        color: "success",
        icon: "mdi-account-check-outline",
      };
    case "account.memberRemoved":
      return {
        category: t("logs.categories.account"),
        action: t("logs.actions.account.memberRemoved", { email: log.data.email }),
        color: "warning",
        icon: "mdi-account-remove-outline",
      };
    case "kycAdminReview.pending":
      return {
        category: t("logs.categories.kycAdminReview"),
        action: t("logs.actions.kycAdminReview.pending"),
        color: "info",
        icon: "mdi-file-document-check-outline",
      };
    case "kycAdminReview.refused":
      return {
        category: t("logs.categories.kycAdminReview"),
        action: t("logs.actions.kycAdminReview.refused", { reason: log.data.reason }),
        color: "error",
        icon: "mdi-file-document-remove-outline",
      };
    case "kycAdminReview.validated":
      return {
        category: t("logs.categories.kycAdminReview"),
        action: t("logs.actions.kycAdminReview.validated"),
        color: "success",
        icon: "mdi-file-document-arrow-right-outline",
      };
    case "email.sent":
      return {
        category: t("logs.categories.email"),
        action: t("logs.actions.email.sent", {
          email: log.data.email,
          template: log.data.template,
        }),
        color: "success",
        icon: "mdi-email-fast-outline",
      };
    case "email.delivered":
      return {
        category: t("logs.categories.email"),
        action: t("logs.actions.email.delivered", {
          email: log.data.email,
          template: log.data.template,
        }),
        color: "success",
        icon: "mdi-email-check-outline",
      };
    case "email.opened":
      return {
        category: t("logs.categories.email"),
        action: t("logs.actions.email.opened", {
          email: log.data.email,
          template: log.data.template,
        }),
        color: "success",
        icon: "mdi-email-open-outline",
      };
    case "email.clicked":
      return {
        category: t("logs.categories.email"),
        action: t("logs.actions.email.clicked", {
          email: log.data.email,
          template: log.data.template,
        }),
        color: "success",
        icon: "mdi-cursor-default-click-outline",
      };
    case "email.spammed":
      return {
        category: t("logs.categories.email"),
        action: t("logs.actions.email.spammed", {
          email: log.data.email,
          template: log.data.template,
        }),
        color: "error",
        icon: "mdi-email-alert-outline",
      };
    case "alert.balanceExceededMax":
      return {
        category: t("logs.categories.alert"),
        action: t("logs.actions.alert.balanceExceededMax", {
          balance: currencyCode.value
            ? formatCurrency({
                value: log.data.balance,
                currencyCode: currencyCode.value,
                locale: locale.value,
              })
            : "",
          threshold:
            log.data.threshold !== null && currencyCode.value
              ? formatCurrency({
                  value: log.data.threshold,
                  currencyCode: currencyCode.value,
                  locale: locale.value,
                })
              : "",
        }),
        color: "warning",
        icon: "mdi-arrow-up-bold-circle-outline",
      };
    case "alert.balanceFallenBelowMin":
      return {
        category: t("logs.categories.alert"),
        action: t("logs.actions.alert.balanceFallenBelowMin", {
          balance: currencyCode.value
            ? formatCurrency({
                value: log.data.balance,
                currencyCode: currencyCode.value,
                locale: locale.value,
              })
            : "",
          threshold:
            log.data.threshold !== null && currencyCode.value
              ? formatCurrency({
                  value: log.data.threshold,
                  currencyCode: currencyCode.value,
                  locale: locale.value,
                })
              : "",
        }),
        color: "warning",
        icon: "mdi-arrow-down-bold-circle-outline",
      };
    case "alert.procedureCollectiveDetected":
      return {
        category: t("logs.categories.alert"),
        action: t("logs.actions.alert.procedureCollectiveDetected", {
          siren: log.data.siren,
        }),
        color: "warning",
        icon: "mdi-alert-outline",
      };
    case "alert.transactionAmountExceeded":
      return {
        category: t("logs.categories.alert"),
        action: t("logs.actions.alert.transactionAmountExceeded", {
          amount: currencyCode.value
            ? formatCurrency({
                value: log.data.amount,
                currencyCode: currencyCode.value,
                locale: locale.value,
              })
            : "",
          threshold: currencyCode.value
            ? formatCurrency({
                value: log.data.threshold,
                currencyCode: currencyCode.value,
                locale: locale.value,
              })
            : "",
        }),
        color: "warning",
        icon: "mdi-arrow-up-bold-circle-outline",
      };
    case "alert.accountInactive":
      return {
        category: t("logs.categories.alert"),
        action: t("logs.actions.alert.accountInactive", {
          since: new Intl.DateTimeFormat(locale.value, {
            dateStyle: "medium",
            timeZone: "Europe/Paris",
          }).format(new Date(log.data.since)),
          days: log.data.days,
        }),
        color: "warning",
        icon: "mdi-sleep",
      };
  }
};

const dateFormatter = new Intl.DateTimeFormat(locale.value, {
  dateStyle: "medium",
  timeStyle: "medium",
});

const formattedLogs = computed(() =>
  logs.value?.map((log) => {
    const logDate = log.createdAt.toDate();
    const details = getLogDetails(log);
    return {
      ...log,
      ...details,
      relativeTime: getRelativeTime(logDate),
      formattedDate: dateFormatter.format(logDate),
    };
  }),
);
</script>
